import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { ReactComponent as CloseIcon } from '../assets/icons/Close-Icon.svg'
import { ReactComponent as InputSearchIcon } from '../assets/input-search-icon.svg'
import styles from './QuickMenuSearch.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { updateHighlightedItems } from '../actions/search'
import { getCurrentItemValues } from '../selectors/items'
import { getSearchQuery } from '../selectors/search'

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_TIME = 175

const QuickMenuSearch = ({ className, hideSearch = () => {} }) => {
  const classNames = cn(styles.quickMenuSearch, className)
  const isMobile = useMemo(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)')

    return mediaQuery.matches
  }, [])

  const debounceTimer = useRef(null)
  const quickSearchRef = useRef(null)
  const query = useSelector(getSearchQuery)
  const menuItems = useSelector(getCurrentItemValues)
  const dispatch = useDispatch()
  const [value, setValue] = useState('')

  const shouldDisplayClose = useMemo(() => {
    if (!isMobile) return true

    return value.length > 0
  }, [isMobile, value])

  const getNewSearchParams = useCallback(
    (query) => {
      const highlightedIds = []
      let focusedItemId

      for (const item of menuItems) {
        if (item.isModifier) continue
        if (!item?.name?.length) continue

        if (item.name.toUpperCase().includes(query.toUpperCase())) {
          highlightedIds.push(item.id)
          if (!focusedItemId) {
            focusedItemId = item.id
          }
        }
      }

      return {
        query,
        highlightedIds,
        focusedItemId,
      }
    },
    [menuItems],
  )

  const closeSearch = () => {
    setValue('')
    dispatch(updateHighlightedItems({ query: '', highlightedIds: [], focusedItemId: null }))

    if (isMobile) {
      quickSearchRef.current.focus.bind(quickSearchRef.current)()
      return
    }

    hideSearch()
  }

  const clearInput = useCallback(
    (cancelSearch = true) => {
      setValue('')
      dispatch(updateHighlightedItems({ query: '', highlightedIds: [], focusedItemId: null }))

      if (cancelSearch) {
        quickSearchRef.current.blur()
        hideSearch()
      }
    },
    [dispatch, hideSearch],
  )

  useEffect(() => {
    return () => {
      // cleanup functions
      clearTimeout(debounceTimer.current)
      dispatch(updateHighlightedItems({ query: '', highlightedIds: [], focusedItemId: null }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (query === value) return
    if (debounceTimer?.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(() => {
      // has minimum search length
      if (value.length >= MIN_QUERY_LENGTH) {
        dispatch(updateHighlightedItems(getNewSearchParams(value)))
        return
      }

      // reset when previous searched and has less than minimum search length
      if (query.length) {
        dispatch(updateHighlightedItems({ query: '', highlightedIds: [], focusedItemId: null }))
      }
    }, DEBOUNCE_TIME)

    return () => clearTimeout(debounceTimer?.current)
  }, [value, query, dispatch, clearInput, getNewSearchParams])

  const handleChange = (event) => setValue(event.target.value)
  const handleBlur = () => {
    if (query.length) return

    clearInput(true)
  }

  return (
    <div className={classNames}>
      <div className={styles.searchContainer}>
        <div className={styles.inputContainer}>
          <div className={styles.searchIcon}>
            <InputSearchIcon />
          </div>
          <input
            className={styles.quickSearchInput}
            type="text"
            data-allow-keyboard-activity
            value={value}
            onBlur={handleBlur}
            autoComplete="off"
            ref={quickSearchRef}
            autoFocus={!isMobile}
            onChange={handleChange}
          />
          {shouldDisplayClose && (
            <div className={styles.clearButton} onClick={closeSearch}>
              <CloseIcon />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default QuickMenuSearch
